/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewContainerRef,
  DOCUMENT,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'

import { BaseFormControlComponent } from '../base-form-control.component'
import { DynamicFormLayout, DynamicFormLayoutService } from '../../../service/dynamic-form-layout.service'
import { DynamicFormControlLayout } from '../../../model/misc/dynamic-form-control-layout.model'
import {
  DYNAMIC_FORM_CONTROL_INPUT_TYPE_DATE,
  DYNAMIC_FORM_CONTROL_INPUT_TYPE_EMAIL,
  DYNAMIC_FORM_CONTROL_INPUT_TYPE_TEL,
  DYNAMIC_FORM_CONTROL_INPUT_TYPE_PASSWORD,
  DynamicInputModel,
} from '../../../model/input/dynamic-input.model'
import { DynamicFormValidationService } from '../../../service/dynamic-form-validation.service'
import { PasswordStrengthMeterComponent } from './password-strength-meter/password-strength-meter.component'

@Component({
  selector: 'dynamic-basic-input',
  templateUrl: './dynamic-basic-input.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DynamicBasicInputComponent extends BaseFormControlComponent implements AfterViewInit {
  private document = inject<Document>(DOCUMENT)
  declare layoutService: DynamicFormLayoutService
  declare validationService: DynamicFormValidationService

  protected readonly DYNAMIC_FORM_CONTROL_INPUT_TYPE_PASSWORD = DYNAMIC_FORM_CONTROL_INPUT_TYPE_PASSWORD
  protected readonly DYNAMIC_FORM_CONTROL_INPUT_TYPE_DATE = DYNAMIC_FORM_CONTROL_INPUT_TYPE_DATE
  protected readonly DYNAMIC_FORM_CONTROL_INPUT_TYPE_EMAIL = DYNAMIC_FORM_CONTROL_INPUT_TYPE_EMAIL
  protected readonly DYNAMIC_FORM_CONTROL_INPUT_TYPE_TEL = DYNAMIC_FORM_CONTROL_INPUT_TYPE_TEL

  @Input() declare formLayout: DynamicFormLayout
  @Input() declare layout: DynamicFormControlLayout
  @Input() declare model: DynamicInputModel
  @Input() declare group: UntypedFormGroup

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() blur: EventEmitter<any> = new EventEmitter()
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() change: EventEmitter<any> = new EventEmitter()
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() focus: EventEmitter<any> = new EventEmitter()

  @ViewChild('strengthMeterViewContainerRef', { read: ViewContainerRef }) viewContainerRef!: ViewContainerRef

  public passwordValue!: string

  // -------------------------------------------------------------------------------------------------------------------
  // @ CONSTRUCTOR
  // -------------------------------------------------------------------------------------------------------------------
  constructor() {
    const layoutService = inject(DynamicFormLayoutService)
    const validationService = inject(DynamicFormValidationService)

    super()

    this.layoutService = layoutService
    this.validationService = validationService
  }

  // -------------------------------------------------------------------------------------------------------------------
  // @ LIFECYCLE-METHODS
  // -------------------------------------------------------------------------------------------------------------------
  public ngAfterViewInit() {
    // We just instantiate and mount the password strength meter in case the schema tells us to
    if (this.model.inputType === 'password' && this.model.displayStrengthMeter) {
      const componentRef = this.viewContainerRef.createComponent(PasswordStrengthMeterComponent)

      // get the password and pass it to the strength meter
      this.group.get(this.model.id)!.valueChanges.subscribe((data) => {
        componentRef.instance.password = data
      })

      this.focus.subscribe(() => {
        componentRef.instance.displayPasswordCriteria()
      })
    }
  }
}
